// JOH World — LEVEL 1: ROAD TO JOH GYM (korte, strakke side-scroller).
import { PAL, VW, Particles, FloatTexts, pixelText } from "./engine";
import { drawFighter, drawGlove } from "./sprites";
import { sfx } from "./audio";

const GROUND = 188;
const WORLD_W = 1700;
const GRAV = 900;
const RUN = 102;
const JUMP_V = -285;
const FLAG_X = 1608;

// Vaste, ontworpen opstelling. Obstakels: klein = 1 stoot, groot = 3 levens (barst → grotere barst → weg)
const ROCKS = [
  { x: 400, size: "small" },
  { x: 868, size: "large" },
  { x: 900, size: "large" },
  { x: 1240, size: "large" },
];
const ROCK_DIMS = { small: { w: 20, h: 18, hp: 1, pts: 10 }, large: { w: 32, h: 34, hp: 3, pts: 40 } };
const ENEMIES = [
  { type: "glovebot", x: 570 },
  { type: "baglet", x: 1030 },
  { type: "dummy", x: 1360 },
];
const FLYER_TRIGGERS = [700, 1180, 1440];

export class Level1 {
  // cb: onScore(points, x, y, label?), onClear(levelScore), onHearts(h)
  constructor(cb) {
    this.cb = cb;
    this.t = 0;
    this.cam = 0;
    this.particles = new Particles();
    this.texts = new FloatTexts();
    this.score = 0;
    this.hearts = 3;
    this.clearing = false;
    this.clearT = 0;
    this.down = false;
    this.downT = 0;
    this.player = { x: 60, y: GROUND, vy: 0, onGround: true, ducking: false, face: 1, attack: null, invulnT: 0, knockT: 0 };
    this.rocks = ROCKS.map((r) => ({ ...r, ...ROCK_DIMS[r.size], lastBump: -9, gone: false }));
    this.enemies = ENEMIES.map((e) => ({ ...e, y: GROUND, hp: 1, dead: false, wob: 0, hopT: Math.random() * 2, dir: -1 }));
    this.flyers = [];
    this.triggers = FLYER_TRIGGERS.map((x) => ({ x, state: "idle", t: 0 }));
    this.flagWave = 0;
  }

  addScore(n, x, y, label) {
    this.score += n;
    if (label) this.texts.add(x, y, label, PAL.yellow, 8);
    this.cb.onScore?.(n);
  }

  hurt() {
    const p = this.player;
    if (p.invulnT > 0 || this.clearing || this.down) return;
    this.hearts -= 1;
    this.cb.onHearts?.(this.hearts);
    sfx.hit();
    this.particles.burst(p.x, p.y - 30, PAL.red, 12, 70);
    p.invulnT = 1.2;
    p.knockT = 0.3;
    p.vy = -120;
    if (this.hearts <= 0) {
      this.down = true;
      this.downT = 0;
      sfx.gameOver();
    }
  }

  tryAttack(kind) {
    const p = this.player;
    if (p.attack || this.clearing || this.down) return;
    p.attack = { kind, t: 0, dur: kind === "jab" ? 0.26 : 0.36, hitDone: false };
    sfx[kind]();
  }

  attackBox() {
    const p = this.player;
    const reach = p.attack.kind === "jab" ? 20 : 30;
    const x0 = p.face === 1 ? p.x : p.x - reach;
    return { x: x0, y: p.y - 44, w: reach, h: 30 };
  }

  // Obstakel raken (aanval of stoot): klein = direct weg, groot = 3 levens met groeiende barsten
  damageRock(r) {
    if (r.gone) return;
    r.hp -= 1;
    if (r.hp <= 0) {
      r.gone = true;
      sfx.rockBreak();
      this.particles.burst(r.x, GROUND - r.h / 2, PAL.greyLight, 18, 95, 0.7, 3);
      this.particles.burst(r.x, GROUND - r.h / 2, PAL.brown, 8, 70);
      this.addScore(r.pts, r.x, GROUND - r.h - 12, `+${r.pts}`);
    } else {
      sfx.rockHit();
      this.particles.burst(r.x, GROUND - r.h / 2, PAL.greyLight, 5, 40, 0.4, 2);
      if (r.size === "large") this.texts.add(r.x, GROUND - r.h - 12, r.hp === 2 ? "CRACK!" : "CRACK!!", PAL.cream, 7, 0.7);
    }
  }

  doAttackHit() {
    const p = this.player;
    const box = this.attackBox();
    const kind = p.attack.kind;
    // enemies
    for (const e of this.enemies) {
      if (e.dead) continue;
      const ew = e.type === "dummy" ? 20 : 22;
      const eh = e.type === "glovebot" ? 26 : e.type === "dummy" ? 46 : 24;
      const ey = e.type === "glovebot" ? e.y - 52 : e.y - eh;
      if (box.x < e.x + ew / 2 && box.x + box.w > e.x - ew / 2 && box.y < e.y && box.y + box.h > ey) {
        e.dead = true;
        sfx.punchHit();
        this.particles.burst(e.x, ey + eh / 2, e.type === "glovebot" ? PAL.red : PAL.brown, 14, 80);
        this.addScore(kind === "jab" ? 50 : 60, e.x, ey - 6, kind === "jab" ? "+50" : "+60");
        return;
      }
    }
    // rocks: elke jab of cross = één stoot (klein breekt direct, groot per leven)
    for (const r of this.rocks) {
      if (r.gone) continue;
      if (box.x < r.x + r.w / 2 && box.x + box.w > r.x - r.w / 2 && box.y + box.h > GROUND - r.h) {
        this.damageRock(r);
        return;
      }
    }
  }

  update(dt, input) {
    this.t += dt;
    this.particles.update(dt);
    this.texts.update(dt);
    const p = this.player;

    if (this.down) {
      this.downT += dt;
      if (this.downT > 1.6) {
        this.down = false;
        this.hearts = 3;
        this.cb.onHearts?.(3);
        p.x = 60; p.y = GROUND; p.vy = 0; p.invulnT = 1.5; p.attack = null;
        this.cam = 0;
        this.flyers = [];
        this.triggers.forEach((tr) => { tr.state = "idle"; tr.t = 0; });
      }
      return;
    }

    if (this.clearing) {
      this.clearT += dt;
      this.flagWave += dt;
      if (this.clearT > 2.6) this.cb.onClear?.(this.score);
      return;
    }

    // ---- movement ----
    const attacking = !!p.attack;
    let mv = 0;
    if (!attacking || !p.onGround) {
      if (input.left) mv -= 1;
      if (input.right) mv += 1;
    }
    if (p.knockT > 0) { p.knockT -= dt; mv = -1.4; }
    if (mv !== 0) p.face = mv > 0 ? 1 : -1;

    p.ducking = input.down && p.onGround && !attacking;

    let nx = p.x + mv * RUN * dt * (p.ducking ? 0 : 1);
    // rock collision (blokkeert horizontale beweging) — ertegen botsen = een stoot (klein breekt direct)
    for (const r of this.rocks) {
      if (r.gone) continue;
      const ph = p.ducking ? 30 : 48;
      if (p.y > GROUND - r.h + 4 && nx + 8 > r.x - r.w / 2 && nx - 8 < r.x + r.w / 2 && p.y - ph < GROUND) {
        nx = p.x; // geblokkeerd
        if (mv !== 0 && this.t - r.lastBump > 0.6) {
          r.lastBump = this.t;
          this.damageRock(r);
        }
      }
    }
    p.x = Math.max(20, Math.min(WORLD_W - 20, nx));

    if (input.jumpEdge && p.onGround && !p.ducking && !attacking) {
      p.vy = JUMP_V;
      p.onGround = false;
      sfx.jump();
    }
    p.vy += GRAV * dt;
    p.y += p.vy * dt;
    const wasAir = !p.onGround;
    if (p.y >= GROUND) {
      p.y = GROUND; p.vy = 0; p.onGround = true;
      if (wasAir) { // landing: stamp + stofwolkje
        sfx.land();
        this.particles.burst(p.x, GROUND, PAL.greyLight, 5, 30, 0.3, 2);
      }
    }

    // voetstappen lopen mee met de loopbeweging
    if (mv !== 0 && p.onGround && !p.ducking && !attacking) {
      this.stepT = (this.stepT || 0) + dt;
      if (this.stepT > 0.26) { this.stepT = 0; sfx.step(); }
    } else {
      this.stepT = 0.18; // bij start direct een stap
    }

    if (input.duckEdge && p.ducking) sfx.duck();

    // ---- attacks ----
    if (input.b1Edge) this.tryAttack("jab");
    if (input.b2Edge) this.tryAttack("cross");
    if (p.attack) {
      p.attack.t += dt;
      if (!p.attack.hitDone && p.attack.t > 0.08) { p.attack.hitDone = true; this.doAttackHit(); }
      if (p.attack.t >= p.attack.dur) p.attack = null;
    }
    if (p.invulnT > 0) p.invulnT -= dt;

    // ---- enemies ----
    for (const e of this.enemies) {
      if (e.dead) continue;
      const dist = p.x - e.x;
      if (e.type === "glovebot") {
        e.wob += dt * 4;
        if (Math.abs(dist) < 130) e.x += Math.sign(dist) * 34 * dt;
      } else if (e.type === "baglet") {
        e.hopT += dt;
        if (Math.abs(dist) < 160) e.x += Math.sign(dist) * 26 * dt;
      } else if (e.type === "dummy") {
        e.wob += dt * 2.2;
      }
      // contact damage
      const ew = 20;
      const pTop = p.y - (p.ducking ? 30 : 48);
      const eBot = e.y;
      const eTop = e.type === "glovebot" ? e.y - 52 : e.y - (e.type === "dummy" ? 46 : 24);
      if (Math.abs(p.x - e.x) < ew && pTop < eBot && p.y > eTop) this.hurt();
    }

    // ---- flying gloves (met waarschuwing) ----
    for (const tr of this.triggers) {
      if (tr.state === "idle" && p.x > tr.x - 150) { tr.state = "warn"; tr.t = 0; sfx.warn(); }
      else if (tr.state === "warn") {
        tr.t += dt;
        if (tr.t > 0.9) {
          tr.state = "done";
          this.flyers.push({ x: this.cam + VW + 20, y: GROUND - 44, vx: -175, ducked: false });
        }
      }
    }
    for (const f of this.flyers) {
      f.x += f.vx * dt;
      // passeert speler
      if (!f.ducked && f.x < p.x - 6) {
        f.ducked = true;
        if (p.ducking) { sfx.niceDuck(); this.addScore(10, p.x, p.y - 52, "NICE DUCK! +10"); }
      }
      // hit?
      const pTop = p.y - (p.ducking ? 30 : 48);
      if (Math.abs(f.x - p.x) < 14 && f.y > pTop && f.y < p.y) this.hurt();
    }
    this.flyers = this.flyers.filter((f) => f.x > this.cam - 60);

    // ---- camera + finish ----
    this.cam = Math.max(0, Math.min(WORLD_W - VW, p.x - 120));
    if (p.x >= FLAG_X) {
      this.clearing = true;
      this.clearT = 0;
      this.addScore(100, FLAG_X, GROUND - 70, "LEVEL 1 +100");
      sfx.levelClear();
    }
  }

  draw(ctx) {
    const cam = this.cam;
    const t = this.t;
    // lucht: warme schemer
    const sky = ctx.createLinearGradient(0, 0, 0, GROUND);
    sky.addColorStop(0, "#17120E");
    sky.addColorStop(0.6, "#33211400");
    sky.addColorStop(1, "#4A2C14");
    ctx.fillStyle = "#1B1410";
    ctx.fillRect(0, 0, VW, 216);
    ctx.fillStyle = sky;
    ctx.globalAlpha = 0.9;
    ctx.fillRect(0, 0, VW, GROUND);
    ctx.globalAlpha = 1;

    // verre skyline (parallax 0.3)
    ctx.fillStyle = "#241C16";
    const skyl = [40, 70, 55, 90, 62, 80, 48, 75, 58, 95, 66, 52, 84, 60];
    for (let i = 0; i < skyl.length; i++) {
      const bx = i * 140 - (cam * 0.3) % 140;
      ctx.fillRect(Math.round(bx), GROUND - 60 - skyl[i] * 0.4, 90, 60 + skyl[i] * 0.4);
    }
    // JOH billboard
    const bbX = 520 - cam * 0.5;
    ctx.fillStyle = PAL.black;
    ctx.fillRect(Math.round(bbX), GROUND - 118, 74, 34);
    ctx.strokeStyle = PAL.yellow;
    ctx.lineWidth = 2;
    ctx.strokeRect(Math.round(bbX), GROUND - 118, 74, 34);
    pixelText(ctx, "JOH.", bbX + 37, GROUND - 108, 12, PAL.yellow, "center");
    pixelText(ctx, "BOXING", bbX + 37, GROUND - 94, 6, PAL.cream, "center");

    // straatlantaarns (parallax 0.6)
    for (let i = 0; i < 8; i++) {
      const lx = i * 260 - (cam * 0.6) % 260;
      ctx.fillStyle = "#3A3430";
      ctx.fillRect(Math.round(lx), GROUND - 78, 4, 78);
      ctx.fillStyle = PAL.yellow;
      ctx.globalAlpha = 0.85;
      ctx.fillRect(Math.round(lx) - 3, GROUND - 84, 10, 7);
      ctx.globalAlpha = 0.14;
      ctx.fillRect(Math.round(lx) - 8, GROUND - 77, 20, 26);
      ctx.globalAlpha = 1;
    }

    // straat
    ctx.fillStyle = "#26262B";
    ctx.fillRect(0, GROUND, VW, 216 - GROUND);
    ctx.fillStyle = PAL.beige;
    for (let i = 0; i < 24; i++) {
      const dx = i * 36 - (cam % 36);
      ctx.fillRect(dx, GROUND + 13, 16, 3);
    }
    ctx.fillStyle = "#17171B";
    ctx.fillRect(0, GROUND, VW, 3);

    // waarschuwings-! voor flyers
    for (const tr of this.triggers) {
      if (tr.state === "warn" && Math.floor(t * 6) % 2 === 0) {
        pixelText(ctx, "!", VW - 26, GROUND - 66, 12, PAL.red, "center");
      }
    }

    // rocks (klein = 1 stoot, groot = 3 levens met groeiende barsten)
    for (const r of this.rocks) {
      if (r.gone) continue;
      const x = Math.round(r.x - cam);
      if (x < -40 || x > VW + 40) continue;
      const hw = r.w / 2;
      ctx.fillStyle = "#3B3B41";
      ctx.fillRect(x - hw, GROUND - r.h, r.w, r.h);
      ctx.fillStyle = "#4C4C54";
      ctx.fillRect(x - hw, GROUND - r.h, r.w, Math.max(4, Math.round(r.h * 0.25)));
      ctx.fillRect(x - hw + 4, GROUND - r.h + 6, Math.max(5, hw / 2), 5);
      const stage = r.size === "large" ? 3 - r.hp : 0; // 1 = kleine barst, 2 = grote barst
      if (stage >= 1) {
        ctx.strokeStyle = "#101014";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x - hw + 6, GROUND - r.h);
        ctx.lineTo(x - 2, GROUND - Math.round(r.h * 0.55));
        ctx.lineTo(x - hw + 8, GROUND - 6);
        ctx.stroke();
      }
      if (stage >= 2) {
        ctx.beginPath();
        ctx.moveTo(x + hw - 6, GROUND - r.h + 2);
        ctx.lineTo(x + 2, GROUND - Math.round(r.h * 0.4));
        ctx.lineTo(x + hw - 10, GROUND - 4);
        ctx.moveTo(x - 3, GROUND - r.h);
        ctx.lineTo(x + 2, GROUND - Math.round(r.h * 0.7));
        ctx.stroke();
      }
    }

    // enemies
    for (const e of this.enemies) {
      if (e.dead) continue;
      const x = Math.round(e.x - cam);
      if (x < -50 || x > VW + 50) continue;
      if (e.type === "glovebot") {
        const hy = Math.round(e.y - 52 + Math.sin(e.wob) * 4);
        ctx.fillStyle = PAL.metal;
        ctx.fillRect(x - 10, hy - 6, 20, 8); // band
        ctx.fillStyle = "#5A5A64";
        ctx.fillRect(x - 3, hy - 12, 6, 6); // antenne
        ctx.fillStyle = PAL.yellow;
        ctx.fillRect(x - 1, hy - 11, 2, 2);
        drawGlove(ctx, x - 9, hy + 2, 2, false);
        ctx.fillStyle = "#6B6B76";
        ctx.fillRect(x - 6, hy + 16, 3, 5); // thrusters
        ctx.fillRect(x + 3, hy + 16, 3, 5);
        ctx.fillStyle = PAL.yellow;
        ctx.globalAlpha = 0.6 + Math.sin(t * 20) * 0.3;
        ctx.fillRect(x - 5, hy + 21, 2, 4);
        ctx.fillRect(x + 4, hy + 21, 2, 4);
        ctx.globalAlpha = 1;
      } else if (e.type === "baglet") {
        const hop = Math.abs(Math.sin(e.hopT * 5)) * 7;
        const by = Math.round(e.y - hop);
        ctx.fillStyle = PAL.brown;
        ctx.fillRect(x - 9, by - 22, 18, 20);
        ctx.fillStyle = PAL.woodDark;
        ctx.fillRect(x - 9, by - 22, 18, 4);
        ctx.fillRect(x - 9, by - 6, 18, 4);
        ctx.fillStyle = PAL.cream;
        ctx.fillRect(x - 4, by - 16, 3, 3); // oogjes
        ctx.fillRect(x + 2, by - 16, 3, 3);
        ctx.fillStyle = PAL.grey;
        ctx.fillRect(x - 7, e.y - 2, 5, 3); // voetjes
        ctx.fillRect(x + 2, e.y - 2, 5, 3);
      } else {
        // training dummy: houten paal + beige torso, wiebelt
        const lean = Math.sin(e.wob) * 0.08;
        ctx.save();
        ctx.translate(x, e.y);
        ctx.rotate(lean);
        ctx.fillStyle = PAL.woodDark;
        ctx.fillRect(-3, -14, 6, 14);
        ctx.fillStyle = PAL.beige;
        ctx.fillRect(-9, -46, 18, 32);
        ctx.fillStyle = PAL.darkRed;
        ctx.fillRect(-9, -30, 18, 5); // band
        ctx.fillStyle = PAL.brown;
        ctx.fillRect(-4, -52, 8, 8); // hoofd
        ctx.restore();
      }
    }

    // flying gloves
    for (const f of this.flyers) {
      drawGlove(ctx, Math.round(f.x - cam), Math.round(f.y), 2, true);
      ctx.fillStyle = PAL.cream;
      ctx.globalAlpha = 0.5;
      ctx.fillRect(Math.round(f.x - cam) + 16, Math.round(f.y) + 3, 8, 2);
      ctx.globalAlpha = 1;
    }

    // JOH vlag (finish)
    const fx = Math.round(FLAG_X - cam);
    ctx.fillStyle = PAL.metal;
    ctx.fillRect(fx, GROUND - 96, 4, 96);
    const wave = Math.round(Math.sin(this.flagWave * 6 + t * 2) * 3);
    ctx.fillStyle = PAL.yellow;
    ctx.beginPath();
    ctx.moveTo(fx + 4, GROUND - 96);
    ctx.lineTo(fx + 44, GROUND - 90 + wave);
    ctx.lineTo(fx + 44, GROUND - 72 + wave);
    ctx.lineTo(fx + 4, GROUND - 66);
    ctx.closePath();
    ctx.fill();
    pixelText(ctx, "JOH", fx + 22, GROUND - 90 + wave, 8, PAL.black, "center");
    pixelText(ctx, "JOH GYM →", fx - 20, GROUND - 116, 7, PAL.cream, "center");

    // speler
    const p = this.player;
    let pose = "idle";
    if (this.down) pose = "hit";
    else if (p.attack) pose = p.attack.kind;
    else if (!p.onGround) pose = "jump";
    else if (p.ducking) pose = "duck";
    else if (Math.abs(p.x - (this._lastX || p.x)) > 0.01) pose = "walk";
    this._lastX = p.x;
    if (this.clearing) pose = "victory";
    drawFighter(ctx, p.x - cam, p.y, 34, pose, t, {
      flip: p.face === -1,
      phase: p.attack ? p.attack.t / p.attack.dur : 0,
      blink: p.invulnT > 0 && !this.down,
    });

    this.particles.draw(ctx, cam);
    this.texts.draw(ctx, cam);

    // DOWN-overlay
    if (this.down) {
      ctx.fillStyle = "rgba(10,10,10,0.6)";
      ctx.fillRect(0, 0, VW, 216);
      pixelText(ctx, "DOWN!", VW / 2, 92, 16, PAL.red, "center");
      pixelText(ctx, "DE TIMER LOOPT DOOR...", VW / 2, 116, 7, PAL.cream, "center");
    }

    // CLEAR-overlay
    if (this.clearing) {
      ctx.fillStyle = "rgba(10,10,10,0.45)";
      ctx.fillRect(0, 0, VW, 216);
      pixelText(ctx, "LEVEL 1 CLEAR!", VW / 2, 80, 14, PAL.yellow, "center");
      pixelText(ctx, "YOU MADE IT TO JOH GYM.", VW / 2, 106, 7, PAL.cream, "center");
    }
  }
}
